/**
 * Profile Page
 *
 * User profile with account information and logout functionality
 */

import { useAuth } from "../contexts/AuthContext";
import { LogOut, Mail, User as UserIcon } from "lucide-react";

export function Profile() {
  const { user, logout } = useAuth();

  const getUserInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = () => {
    if (confirm("Are you sure you want to logout?")) {
      logout();
    }
  };

  return (
    <div className="h-full flex flex-col bg-[#FAF6F1] dark:bg-[#1A2D47]">
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto p-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-[#1D1B19] dark:text-[#E3E6EA] text-3xl font-semibold mb-2">
              Profile
            </h1>
            <p className="text-[#8E8378] dark:text-[#9AAABF] text-base">
              Your account information
            </p>
          </div>

          {/* Divider */}
          <div className="w-full h-[1px] bg-[#E5DDD3] dark:bg-[#2C405F] mb-8" />

          {/* Profile Card */}
          <div className="bg-white dark:bg-[#122135] rounded-2xl border border-[#E5DDD3] dark:border-[#2C405F] p-6 mb-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-start gap-6 mb-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {user?.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-24 h-24 rounded-full object-cover border-2 border-[#E5DDD3] dark:border-[#455D7D]"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#2C405F] to-[#6E829E] dark:from-[#6E829E] dark:to-[#9AAABF] flex items-center justify-center border-2 border-[#E5DDD3] dark:border-[#455D7D]">
                    <span className="text-white text-3xl font-bold">
                      {getUserInitials(user?.name)}
                    </span>
                  </div>
                )}
              </div>

              {/* User Info */}
              <div className="flex-1">
                <h2 className="text-[#1D1B19] dark:text-[#E3E6EA] text-2xl font-semibold mb-2">
                  {user?.name || "User"}
                </h2>
                <div className="flex items-center gap-2 text-[#8E8378] dark:text-[#9AAABF] text-sm mb-3">
                  <Mail size={16} />
                  <span>{user?.email || "No email"}</span>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="w-full h-[1px] bg-[#E5DDD3] dark:bg-[#2C405F] my-6" />

            {/* Account Details */}
            <div className="space-y-4">
              <h3 className="text-[#1D1B19] dark:text-[#E3E6EA] text-lg font-semibold mb-4">
                Account Details
              </h3>

              {/* User ID */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FDFAF7] dark:bg-[#1A2D47] border border-[#E5DDD3] dark:border-[#455D7D]">
                <div className="flex-shrink-0 mt-1">
                  <UserIcon
                    size={20}
                    className="text-[#8E8378] dark:text-[#9AAABF]"
                  />
                </div>
                <div className="flex-1">
                  <div className="text-[#8E8378] dark:text-[#9AAABF] text-sm mb-1">
                    User ID
                  </div>
                  <div className="text-[#1D1B19] dark:text-[#E3E6EA] text-sm font-mono break-all">
                    {user?.id || "N/A"}
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-3 p-4 rounded-xl bg-[#FDFAF7] dark:bg-[#1A2D47] border border-[#E5DDD3] dark:border-[#455D7D]">
                <div className="flex-shrink-0 mt-1">
                  <Mail
                    size={20}
                    className="text-[#8E8378] dark:text-[#9AAABF]"
                  />
                </div>
                <div className="flex-1">
                  <div className="text-[#8E8378] dark:text-[#9AAABF] text-sm mb-1">
                    Email Address
                  </div>
                  <div className="text-[#1D1B19] dark:text-[#E3E6EA] text-sm break-all">
                    {user?.email || "N/A"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Logout Button */}
          <div className="flex justify-center">
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 text-white font-medium transition-all hover:scale-[1.02] active:scale-[0.98] shadow-lg"
            >
              <LogOut size={20} />
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
